import {
  ChangeDetectionStrategy,
  Component,
  inject,
  signal,
} from "@angular/core";
import { FormsModule } from "@angular/forms";
import { RouterModule } from "@angular/router";
import { AuthService } from "../core/auth.service";

@Component({
  selector: "app-change-password-page",
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [FormsModule, RouterModule],
  templateUrl: "./change-password-page.component.html",
  styleUrls: ["./change-password-page.component.scss"],
})
export class ChangePasswordPageComponent {
  private readonly auth = inject(AuthService);

  currentPassword = "";
  newPassword = "";
  confirmPassword = "";

  readonly error = signal<string | null>(null);
  readonly success = signal<string | null>(null);
  readonly saving = signal(false);

  async submit() {
    this.error.set(null);
    this.success.set(null);
    if (!this.currentPassword) {
      this.error.set("Enter your current password.");
      return;
    }
    if (this.newPassword.length < 8) {
      this.error.set("New password must be at least 8 characters.");
      return;
    }
    if (this.newPassword !== this.confirmPassword) {
      this.error.set("New passwords do not match.");
      return;
    }
    if (this.newPassword === this.currentPassword) {
      this.error.set("New password must be different from the current one.");
      return;
    }
    this.saving.set(true);
    try {
      await this.auth.changePassword(this.currentPassword, this.newPassword);
      this.success.set("Password updated. Use it next time you sign in.");
      this.currentPassword = "";
      this.newPassword = "";
      this.confirmPassword = "";
    } catch (e) {
      this.error.set(this.friendlyError(e));
    } finally {
      this.saving.set(false);
    }
  }

  private friendlyError(e: unknown): string {
    const code = (e as { code?: string })?.code ?? "";
    const message = (e as Error)?.message ?? "";
    if (code === "NotAuthorizedException" || /incorrect/i.test(message)) {
      return "Current password is incorrect.";
    }
    if (code === "InvalidPasswordException" || /policy|requirements/i.test(message)) {
      return "New password must be 8+ characters and include an uppercase letter, a lowercase letter, a number, and a symbol.";
    }
    if (code === "LimitExceededException") {
      return "Too many attempts. Please wait a moment and try again.";
    }
    return message || "Failed to update password. Please try again.";
  }
}
